import React, { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { Tooltip } from "@mui/material";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../context/useGlobalState";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import Button from "../../../../components/ui/Button";
import { FileInput } from "../../../../components/common/FileInput";
import Dropdown from "../../../../components/common/Dropdown";
import FormGroup from "../../../../components/common/FormGroup";
import { saveTemporaryWaypoint } from "../../../../api/waypoint";
import { getProjects, getProject } from "../../../../api/project";
import { uploadFileToS3 } from "../../../../api/s3";
import stepStyles from "../createStep.module.css";
import styles from "./CreateContentPlaylistItem.module.css";

interface CreateContentPlaylistItemProps {
    navigateToNext: () => void;
}

interface LocalizedContent {
    name: string;
    text: string;
    audioUrl: string;
}

const CreateContentPlaylistItem: React.FC<CreateContentPlaylistItemProps> = ({ navigateToNext }) => {
    const { trackData, updateTrackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";
    const { data: globalState } = useGlobalState();

    // Form state
    const [pictures, setPictures] = useState<string[]>([]);
    const [position, setPosition] = useState<number>(1);

    // Language tabs state
    const [activeLanguageTab, setActiveLanguageTab] = useState<number>(0);
    const [localizedContent, setLocalizedContent] = useState<Record<string, LocalizedContent>>({});
    const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);

    // Projects for "Add from projects" feature - Text section
    const [projects, setProjects] = useState<any[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string>("");
    const [projectStories, setProjectStories] = useState<any[]>([]);
    const [selectedProjectStoryId, setSelectedProjectStoryId] = useState<string>("");

    // Projects for "Add from projects" feature - Audio section
    const [selectedProjectIdForAudio, setSelectedProjectIdForAudio] = useState<string>("");
    const [projectStoriesForAudio, setProjectStoriesForAudio] = useState<any[]>([]);
    const [selectedProjectStoryIdForAudio, setSelectedProjectStoryIdForAudio] = useState<string>("");

    // Loading states
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadingPicture, setUploadingPicture] = useState<boolean>(false);
    const [uploadingAudio, setUploadingAudio] = useState<boolean>(false);
    const [loadingProjects, setLoadingProjects] = useState<boolean>(false);

    const linkId = trackData?.trackId || trackData?.id;

    // Initialize available languages from track
    useEffect(() => {
        const initLanguages = () => {
            if (availableLanguages.length > 0) return;

            let langs: string[] = [];
            if (trackData?.availableLanguages && trackData.availableLanguages.length > 0) {
                langs = trackData.availableLanguages;
            } else if (trackData?.language) {
                langs = [trackData.language];
            } else {
                langs = ["FR"];
            }
            setAvailableLanguages(langs);

            setLocalizedContent((prev) => {
                const newContent = { ...prev };
                langs.forEach((lang) => {
                    if (!newContent[lang]) {
                        newContent[lang] = { name: "", text: "", audioUrl: "" };
                    }
                });
                return newContent;
            });
        };

        initLanguages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData?.language]);

    // Load projects
    useEffect(() => {
        const fetchProjects = async () => {
            setLoadingProjects(true);
            try {
                const response = await getProjects({ token });
                if (response) {
                    setProjects(response);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                setLoadingProjects(false);
            }
        };

        fetchProjects();
    }, [token]);

    // Load stories from selected project (text section)
    useEffect(() => {
        const fetchProjectStories = async () => {
            if (!selectedProjectId) {
                setProjectStories([]);
                return;
            }

            try {
                const project = await getProject({ token, projectId: selectedProjectId });
                if (project?.audioAI) {
                    setProjectStories(
                        project.audioAI.filter(
                            (s: any) => (s.text && s.text.trim()) || (s.content && s.content.trim()),
                        ),
                    );
                }
            } catch (error) {
                console.error("Error fetching project stories:", error);
            }
        };

        fetchProjectStories();
    }, [selectedProjectId, token]);

    // Load stories from selected project (audio section)
    useEffect(() => {
        const fetchProjectStoriesForAudio = async () => {
            if (!selectedProjectIdForAudio) {
                setProjectStoriesForAudio([]);
                return;
            }

            try {
                const project = await getProject({ token, projectId: selectedProjectIdForAudio });
                if (project?.audioAI) {
                    setProjectStoriesForAudio(project.audioAI.filter((s: any) => s.audioUrl));
                }
            } catch (error) {
                console.error("Error fetching project stories for audio:", error);
            }
        };

        fetchProjectStoriesForAudio();
    }, [selectedProjectIdForAudio, token]);

    useEffect(() => {
        setSelectedProjectId("");
        setSelectedProjectStoryId("");
        setSelectedProjectIdForAudio("");
        setSelectedProjectStoryIdForAudio("");
    }, [activeLanguageTab]);

    const getLanguageName = (code: string) => {
        const lang = globalState?.languagesAI?.find((l: any) => l.code === code || l.value === code);
        return lang?.label || code;
    };

    const creationLanguage = availableLanguages[0] || trackData?.language || "FR";
    const currentLanguage = availableLanguages[activeLanguageTab] || creationLanguage;

    const projectOptions = projects
        .filter((p: any) => p.audioAI?.some((a: any) => a.language?.toUpperCase() === currentLanguage?.toUpperCase()))
        .map((p: any) => ({
            value: p.id,
            label: p.name || t("unnamed"),
        }));

    const storyOptions = projectStories
        .filter((s: any) => s.language?.toUpperCase() === currentLanguage?.toUpperCase())
        .map((s: any) => ({
            value: s.id,
            label: s.name || s.title || t("unnamed"),
        }));

    const storyOptionsForAudio = projectStoriesForAudio
        .filter((s: any) => s.language?.toUpperCase() === currentLanguage?.toUpperCase())
        .map((s: any) => ({
            value: s.id,
            label: s.name || s.title || t("unnamed"),
        }));

    const unusedLanguages = useMemo(() => {
        const allLanguages = globalState?.languagesAI || [];
        return allLanguages.filter((lang: any) => !availableLanguages.includes(lang.code));
    }, [globalState?.languagesAI, availableLanguages]);

    const [showLanguageSelector, setShowLanguageSelector] = useState(false);

    const handleLanguageTabChange = (newValue: number) => {
        setActiveLanguageTab(newValue);
    };

    const handleAddLanguage = (langCode: string) => {
        if (!langCode || availableLanguages.includes(langCode)) return;

        setLocalizedContent((prev) => ({
            ...prev,
            [langCode]: { name: "", text: "", audioUrl: "" },
        }));

        const newLanguages = [...availableLanguages, langCode];
        setAvailableLanguages(newLanguages);
        setActiveLanguageTab(newLanguages.length - 1);
        setShowLanguageSelector(false);
    };

    const handleRemoveLanguage = (langCode: string) => {
        if (langCode === creationLanguage) return;

        const newLanguages = availableLanguages.filter((l) => l !== langCode);
        setAvailableLanguages(newLanguages);

        setLocalizedContent((prev) => {
            const updated = { ...prev };
            delete updated[langCode];
            return updated;
        });

        if (activeLanguageTab >= newLanguages.length) {
            setActiveLanguageTab(Math.max(0, newLanguages.length - 1));
        }
    };

    const handleContentChange = (field: keyof LocalizedContent, value: string) => {
        setLocalizedContent((prev) => ({
            ...prev,
            [currentLanguage]: {
                ...prev[currentLanguage],
                [field]: value,
            },
        }));
    };

    const handlePictureUpload = async (files: File[] | File) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setUploadingPicture(true);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setPictures((prev) => [...prev, response.url]);
            }
        } catch (error) {
            console.error("Error uploading picture:", error);
        } finally {
            setUploadingPicture(false);
        }
    };

    const handleRemovePicture = (index: number) => {
        setPictures((prev) => prev.filter((_, i) => i !== index));
    };

    const handleAudioUpload = async (files: File[] | File) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setUploadingAudio(true);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                handleContentChange("audioUrl", response.url);
            }
        } catch (error) {
            console.error("Error uploading audio:", error);
        } finally {
            setUploadingAudio(false);
        }
    };

    const importTextFromStory = (storyId: string) => {
        const story = projectStories.find((s) => s.id === storyId);
        if (!story) return;
        handleContentChange("text", story.content || story.text || "");
        if (story.title || story.name) {
            handleContentChange("name", story.title || story.name);
        }
    };

    const importAudioFromStory = (storyId: string) => {
        const story = projectStoriesForAudio.find((s) => s.id === storyId);
        if (story?.audioUrl) {
            handleContentChange("audioUrl", story.audioUrl);
        }
    };

    const handleCancel = () => {
        navigate(`/create/tour/${linkId}/content-playlist`);
    };

    const handleSave = async () => {
        if (!trackData.trackId) return;

        const hasContent = Object.values(localizedContent).some((c) => c.name.trim());
        if (!hasContent) return;

        setLoading(true);
        try {
            const internalId = uuidv4();
            const primaryLang = availableLanguages[0];
            const primaryContent = localizedContent[primaryLang];

            await saveTemporaryWaypoint({
                token,
                internalId,
                trackId: trackData.trackId,
                language: primaryLang,
                title: primaryContent.name,
                text: primaryContent.text,
                audioUrl: primaryContent.audioUrl || undefined,
                pictures: pictures.length > 0 ? pictures : undefined,
                type: "INVISIBLE",
                triggerMode: "MANUAL",
                scannableObjectId: undefined,
                position,
                latitude: 0,
                longitude: 0,
                localizedContent: localizedContent,
            });

            updateTrackData("availableLanguages", availableLanguages);
            navigateToNext();
        } catch (error) {
            console.error("Error creating content playlist item:", error);
        } finally {
            setLoading(false);
        }
    };

    const currentContent = localizedContent[currentLanguage] || { name: "", text: "", audioUrl: "" };

    // Form validation: position required, name required, audio required for primary language
    const isFormValid = useMemo(() => {
        if (!position) return false;
        const primaryContent = localizedContent[availableLanguages[0]];
        if (!primaryContent?.name?.trim()) return false;
        if (!primaryContent?.audioUrl) return false;
        return true;
    }, [position, localizedContent, availableLanguages]);

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${styles.root}`}>
            <div className={styles.crumb}>
                <Link to={`/create/tour/${linkId}/content-playlist`}>{t("contentPlaylist")}</Link>
                <span className={styles.crumbSep}>&rsaquo;</span>
                <span className={styles.crumbCurrent}>{t("createContentPlaylistItem")}</span>
            </div>

            <TitleSubtitle
                title={t("createContentPlaylistItem")}
                subtitle={t("createContentPlaylistItemSubtitle")}
                classNameTitle={styles.pageTitle}
                classNameSubtitle={styles.pageSub}
            />

            <div className={stepStyles.stepDescription}>
                <FormGroup title={t("images")} subtitle={t("imagesWillIllustrateStory")}>
                    {pictures.length > 0 && (
                        <div className={styles.picRow}>
                            {pictures.map((pic, index) => (
                                <div key={index} className={styles.picThumb}>
                                    <img src={pic} alt={`${t("picture")} ${index + 1}`} />
                                    <button
                                        onClick={() => handleRemovePicture(index)}
                                        type="button"
                                        className={styles.picRemove}
                                        aria-label={t("remove")}
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <FileInput
                        onFileSelected={handlePictureUpload}
                        placeholder={uploadingPicture ? t("uploading") : t("dragAndDropOrClick")}
                        disabled={uploadingPicture}
                        acceptedFileTypes={{ "image/*": [".png", ".jpg", ".jpeg", ".webp"] }}
                    />
                </FormGroup>

                <FormGroup title={t("position")} subtitle={t("positionOrderInApp")} required>
                    <div className={styles.posInput}>
                        <TextInput
                            placeholder="1"
                            value={position.toString()}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                setPosition(parseInt(e.target.value) || 1)
                            }
                            size="small"
                            type="number"
                        />
                    </div>
                </FormGroup>

                <FormGroup title={t("contentByLanguage")} subtitle={t("contentByLanguageDescription")}>
                    <div className={styles.langTabs}>
                        {availableLanguages.map((lang, index) => (
                            <button
                                key={lang}
                                type="button"
                                className={`${styles.langTab} ${activeLanguageTab === index ? styles.langTabOn : ""}`}
                                onClick={() => handleLanguageTabChange(index)}
                            >
                                <span>{getLanguageName(lang)}</span>
                                {lang !== creationLanguage && (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        className={styles.langTabRemove}
                                        title={t("removeLanguage")}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveLanguage(lang);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.stopPropagation();
                                                handleRemoveLanguage(lang);
                                            }
                                        }}
                                    >
                                        ✕
                                    </span>
                                )}
                            </button>
                        ))}

                        {unusedLanguages.length > 0 &&
                            (showLanguageSelector ? (
                                <div className={styles.langAddSelect}>
                                    <select
                                        defaultValue=""
                                        onChange={(e) => {
                                            if (e.target.value) handleAddLanguage(e.target.value);
                                        }}
                                    >
                                        <option value="" disabled>
                                            {t("selectLanguage")}
                                        </option>
                                        {unusedLanguages.map((lang: any) => (
                                            <option key={lang.code} value={lang.code}>
                                                {lang.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className={styles.langAddCancel}
                                        onClick={() => setShowLanguageSelector(false)}
                                    >
                                        {t("cancel")}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.langAdd}
                                    onClick={() => setShowLanguageSelector(true)}
                                >
                                    + {String(t("addLanguage")).replace(/^./, (c: string) => c.toLowerCase())}
                                </button>
                            ))}
                    </div>

                    <div className={styles.fieldBlock}>
                        <div className={styles.sublbl}>
                            {t("name")} <span className={styles.req}>*</span>
                        </div>
                        <div className={styles.subhelp}>
                            {t("enterNameIn", { language: getLanguageName(currentLanguage) })}
                        </div>
                        <div className={styles.nameInput}>
                            <TextInput
                                placeholder={t("enterStoryName")}
                                value={currentContent.name}
                                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                    handleContentChange("name", e.target.value)
                                }
                                size="medium"
                            />
                        </div>
                    </div>

                    <div className={styles.fieldBlock}>
                        <div className={styles.sublbl}>{t("text")}</div>
                        <div className={styles.subhelp}>
                            {t("enterTextIn", { language: getLanguageName(currentLanguage) })}
                        </div>
                        <div className={styles.textArea}>
                            <TextInput
                                placeholder={t("enterStoryDescription")}
                                value={currentContent.text}
                                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                    handleContentChange("text", e.target.value)
                                }
                                size="big"
                                numberOfLines={6}
                            />
                        </div>

                        <div className={styles.fromProj}>{t("addFromProjects")}</div>
                        <div className={styles.projRow}>
                            <Tooltip title={projectOptions.length === 0 ? t("tooltipNoProjectsInLanguage") : ""}>
                                <span>
                                    <Dropdown
                                        label=""
                                        value={selectedProjectId}
                                        options={projectOptions}
                                        onChange={(val) => {
                                            setSelectedProjectId(val);
                                            setSelectedProjectStoryId("");
                                        }}
                                        placeholder={t("selectProject")}
                                        loading={loadingProjects}
                                        error={false}
                                        disabled={projectOptions.length === 0}
                                        isWithoutMargin
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={
                                    !selectedProjectId
                                        ? t("tooltipSelectProjectFirst")
                                        : storyOptions.length === 0
                                          ? t("tooltipNoStoriesWithText")
                                          : ""
                                }
                            >
                                <span>
                                    <Dropdown
                                        label=""
                                        value={selectedProjectStoryId}
                                        options={storyOptions}
                                        onChange={(val) => {
                                            setSelectedProjectStoryId(val);
                                            if (val) importTextFromStory(val);
                                        }}
                                        placeholder={t("selectStory")}
                                        loading={false}
                                        error={false}
                                        disabled={!selectedProjectId || storyOptions.length === 0}
                                        isWithoutMargin
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </div>

                    <div className={`${styles.fieldBlock} ${styles.fieldBlockAudio}`}>
                        <div className={styles.sublbl}>
                            {t("audio")} <span className={styles.req}>*</span>
                        </div>
                        <div className={styles.subhelp}>
                            {t("insertAudioIn", { language: getLanguageName(currentLanguage) })}
                        </div>
                        {currentContent.audioUrl && (
                            <div className={styles.audioPreview}>
                                <audio controls src={currentContent.audioUrl} />
                                <button
                                    type="button"
                                    className={styles.removeAudio}
                                    onClick={() => handleContentChange("audioUrl", "")}
                                >
                                    {t("remove")} {t("audio")}
                                </button>
                            </div>
                        )}
                        <FileInput
                            key={`audio-${currentLanguage}`}
                            onFileSelected={handleAudioUpload}
                            placeholder={uploadingAudio ? t("uploading") : t("dragAndDropOrClick")}
                            disabled={uploadingAudio}
                            acceptedFileTypes={{ "audio/*": [".mp3", ".wav", ".m4a"] }}
                        />

                        <div className={styles.fromProj}>{t("addFromProjects")}</div>
                        <div className={styles.projRow}>
                            <Tooltip title={projectOptions.length === 0 ? t("tooltipNoProjectsInLanguage") : ""}>
                                <span>
                                    <Dropdown
                                        label=""
                                        value={selectedProjectIdForAudio}
                                        options={projectOptions}
                                        onChange={(val) => {
                                            setSelectedProjectIdForAudio(val);
                                            setSelectedProjectStoryIdForAudio("");
                                        }}
                                        placeholder={t("selectProject")}
                                        loading={loadingProjects}
                                        error={false}
                                        disabled={projectOptions.length === 0}
                                        isWithoutMargin
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={
                                    !selectedProjectIdForAudio
                                        ? t("tooltipSelectProjectFirst")
                                        : storyOptionsForAudio.length === 0
                                          ? t("tooltipNoStoriesWithAudio")
                                          : ""
                                }
                            >
                                <span>
                                    <Dropdown
                                        label=""
                                        value={selectedProjectStoryIdForAudio}
                                        options={storyOptionsForAudio}
                                        onChange={(val) => {
                                            setSelectedProjectStoryIdForAudio(val);
                                            if (val) importAudioFromStory(val);
                                        }}
                                        placeholder={t("selectStory")}
                                        loading={false}
                                        error={false}
                                        disabled={!selectedProjectIdForAudio || storyOptionsForAudio.length === 0}
                                        isWithoutMargin
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                </FormGroup>
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("cancel")}
                    onClick={handleCancel}
                    variant="cancel"
                    shape="pill"
                    className={stepStyles.footerBtn}
                />
                <Button
                    label={loading ? t("saving") : t("create")}
                    onClick={handleSave}
                    disabled={!isFormValid || loading || uploadingPicture || uploadingAudio}
                    variant="primary"
                    shape="pill"
                    className={`${stepStyles.continueBtn} ${stepStyles.footerBtn}`}
                />
            </div>
        </div>
    );
};

export default CreateContentPlaylistItem;
